/**
 * One place that decides what every licensing state is called and what colour
 * it is drawn in.
 *
 * The server sends keys (`paid_yearly`, `grace`, `trial_1d`); everything the
 * reader sees is decided here, so a chip, a flow node, a bar segment and a
 * table cell can never disagree about what "grace" means or looks like.
 *
 * Colour carries meaning rather than decoration: money is green, trials are
 * the platform's teal, anything that needs attention is clay, and what has
 * already ended is grey. Two states never share a hue.
 */

export const BILLING = {
  paid_monthly: { label: "Monthly", short: "Monthly", color: "#227f5d", tone: "good" },
  paid_yearly: { label: "Yearly", short: "Yearly", color: "#14563f", tone: "good" },
  cancelling: { label: "Won't renew", short: "Won't renew", color: "#c9972b", tone: "warn" },
  grace: { label: "Payment failed", short: "Grace", color: "#b5563b", tone: "risk" },
  lapsed: { label: "Ended", short: "Ended", color: "#8a9992", tone: "muted" },
  refunded: { label: "Refunded", short: "Refunded", color: "#6f42c1", tone: "muted" },
  app_store: { label: "App Store", short: "App Store", color: "#1f6f8b", tone: "good" },
  paid_no_record: { label: "Paid, no payment on file", short: "No payment on file", color: "#a33b6b", tone: "risk" },
  none: { label: "Free", short: "Free", color: "#c9d3ce", tone: "muted" },
  other: { label: "Other", short: "Other", color: "#5b6b64", tone: "muted" },
};

export const ACCESS = {
  grandfathered: { label: "Early users (free)", color: "#5b6b64" },
  individual: { label: "Paid plan", color: "#227f5d" },
  school_seat: { label: "School licence", color: "#1f6f8b" },
  trial: { label: "On trial", color: "#c9972b" },
  none: { label: "No access", color: "#9fb0a8" },
};

export const TRIAL = {
  trial_1d: { label: "1 day left", color: "#b5563b" },
  trial_3d: { label: "2–3 days left", color: "#c9972b" },
  trial_active: { label: "On trial", color: "#1f6f8b" },
  trial_converted: { label: "Paid after trial", color: "#227f5d" },
  trial_expired: { label: "Trial ended, didn't pay", color: "#8a9992" },
  never_trialled: { label: "No trial", color: "#c9d3ce" },
};

export const RISK = {
  grace: { label: "Payment failed", detail: "Card declined. We retry for a few days." },
  cancelling_30d: { label: "Ending soon", detail: "Turned off auto-renew. Ends within 30 days." },
  paid_no_record: { label: "Paid, no payment on file", detail: "Has premium, but nothing will renew it." },
  renewal_overdue: { label: "Renewal late", detail: "Should have renewed, but hasn't yet." },
  no_card: { label: "No saved card", detail: "The next renewal can't be charged." },
};

export const ACTIVITY_LABELS = {
  TRIAL_STARTED: "started a trial",
  PAYMENT_RECORDED: "paid",
  PAYMENT_REFUNDED: "was refunded",
  PAYMENT_FAILED: "had a payment fail",
  RENEWAL_FAILED: "had a renewal fail",
  LICENSE_RENEWED: "renewed",
  LICENSE_EXPIRED: "plan ended",
  LICENSE_CANCELLED: "cancelled",
  LICENSE_CREATED: "bought a school licence",
  LICENSE_AUTO_RENEW_CHANGED: "changed auto-renew",
};

export const ACTIVITY_CATEGORIES = {
  trials: { label: "Trials started", detail: "Teachers who started the free 14-day trial.", color: "#1f6f8b" },
  payments: { label: "Payments", detail: "New plans, renewals and school licences.", color: "#227f5d" },
  cancellations: { label: "Auto-renew changes", detail: "Auto-renew turned off or on, and plans that ended.", color: "#c9972b" },
  failures: { label: "Failed payments", detail: "Declined cards and failed renewals.", color: "#b5563b" },
  refunds: { label: "Refunds", detail: "Money returned.", color: "#6f42c1" },
};

const FALLBACK = { label: "Unknown", color: "#8a9992", tone: "muted" };

export const billing = (key) => BILLING[key] || { ...FALLBACK, label: key || "Unknown" };
export const access = (key) => ACCESS[key] || FALLBACK;
export const trial = (key) => TRIAL[key] || FALLBACK;

/** Money always arrives in cents with the currency it was charged in. */
export function formatMoney(cents, currency = "USD", { compact = false } = {}) {
  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      notation: compact && Math.abs(amount) >= 10000 ? "compact" : "standard",
      maximumFractionDigits: compact || Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    // An unknown currency code must not take the panel down with it.
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value) || 0);
}

export function formatPercent(fraction) {
  if (fraction === null || fraction === undefined) return "—";
  return `${Math.round(fraction * 1000) / 10}%`;
}

/** Dates come as ISO UTC; people read them in their own timezone. */
export function formatDate(iso, { withYear = false } = {}) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/** "in 4 days" / "3 days ago", for a column of dates that are mostly near today. */
export function relativeDays(iso) {
  if (!iso) return "—";
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}
